// src/hooks/useSpecialtyCart.js
// ═══════════════════════════════════════════════════════════════════
// Shared login-gated Add to Cart logic for homepage product-teaser
// sections (Specialities/Best Sellers, Menu tabs) across every template
// except Template 16, which keeps its own original implementation.
// Extracted from Template 16's SweetStoreSpecialMenu (src/components/
// SpecialMenu/index.js) — the reference implementation — so the same real
// cart-write + login-gate behavior is available to every template's own
// existing button, without duplicating the logic per template or per file.
// Weight-based items use the shop page's existing WeightSelectionModal
// instead of a bespoke dropdown, since most templates' cards have no room
// of their own for one and reusing an already-built component is safer
// than adding new UI per template.
// ═══════════════════════════════════════════════════════════════════
import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrderFlow } from '../context/OrderFlowContext';
import { checkStoreAvailability, getStoreStatus } from '../components/checkStoreAvailability';
import { getValidAuthSession } from '../utils/authStorage';
import AuthModal from '../components/AuthModal';
import WeightSelectionModal from '../components/WeightSelectionModal';
import StoreClosedModal from '../components/StoreClosedModal';

// Mirrors RestaurantMenuApp's own cart storage so an item added from the
// homepage lands in the exact same cart the shop page reads from.
const CART_STORAGE_KEY = 'resto_cart_data';
const getCartKey = (storeId, orderType) => `${CART_STORAGE_KEY}_${storeId}_${orderType}`;

export const useSpecialtyCart = (nearestStore) => {
  const navigate = useNavigate();
  const { selectedStore: contextStore, orderType, isPreOrder, setStore, setPreOrder } = useOrderFlow();
  const effectiveOrderType = orderType || 'delivery';
  const effectiveStore = contextStore || nearestStore;

  const getStoreClosedStatus = () => {
    if (!effectiveStore?.week_schedule) return { isClosed: false, message: '' };
    const availability = checkStoreAvailability(effectiveStore.week_schedule);
    const supportsPreOrder = Boolean(effectiveStore?.allow_pre_order || effectiveStore?.pre_order_enabled);
    const isClosed = Boolean(availability && !availability.isOpen && !isPreOrder && !supportsPreOrder);
    return { isClosed, message: availability?.message || 'Store is currently closed' };
  };

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showStoreClosedModal, setShowStoreClosedModal] = useState(false);
  const [weightModalItem, setWeightModalItem] = useState(null);
  const pendingActionRef = useRef(null);

  const isLoggedIn = () => !!getValidAuthSession()?.userId;

  const persistCartItem = (cartItem) => {
    if (!effectiveStore?.id) return;
    if (!contextStore || contextStore.id !== effectiveStore.id) {
      setStore(effectiveStore);
    }
    const key = getCartKey(effectiveStore.id, effectiveOrderType);
    let existingItems = [];
    try {
      const stored = JSON.parse(localStorage.getItem(key) || 'null');
      existingItems = stored?.items || [];
    } catch {
      existingItems = [];
    }
    const idx = existingItems.findIndex(it => it.cartItemKey === cartItem.cartItemKey);
    let updatedItems;
    if (idx >= 0) {
      updatedItems = [...existingItems];
      updatedItems[idx] = { ...updatedItems[idx], quantity: (updatedItems[idx].quantity || 1) + 1 };
    } else {
      updatedItems = [...existingItems, cartItem];
    }
    localStorage.setItem(key, JSON.stringify({
      items: updatedItems,
      timestamp: Date.now(),
      storeId: effectiveStore.id,
      orderType: effectiveOrderType
    }));
    window.dispatchEvent(new Event('cart_updated'));
  };

  const addSimpleItem = (item) => {
    persistCartItem({
      id: item.id,
      name: item.title,
      price: item.priceValue,
      flatPrice: item.priceValue,
      image: item.image,
      description: item.description || '',
      quantity: 1,
      variations: [],
      extras: [],
      addons: [],
      cartItemKey: String(item.id),
      is_tracked: item.isTracked,
      is_available: item.isAvailable,
      category: item.categoryName
    });
  };

  const addWeightedItem = (item, option) => {
    const calculatedPrice = option.value * item.priceValue;
    persistCartItem({
      id: item.id,
      name: item.title,
      image: item.image,
      description: item.description || '',
      selectedWeight: option.value,
      weightUnit: item.weightUnitLabel,
      flatPrice: calculatedPrice,
      price: calculatedPrice,
      isWeightBased: true,
      weightLabel: option.label,
      quantity: 1,
      variations: [],
      extras: [],
      addons: [],
      cartItemKey: `${item.id}|w:${option.value}`,
      category: item.categoryName
    });
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    // Bridges an independent login flow to the Header's top-level auth
    // state — same event App.js already listens for.
    window.dispatchEvent(new Event('auth_updated'));
    const pending = pendingActionRef.current;
    pendingActionRef.current = null;
    if (!pending) return;
    if (pending.type === 'simple') addSimpleItem(pending.item);
    else if (pending.type === 'weighted') addWeightedItem(pending.item, pending.option);
  };

  const handleAddToCart = (item, e) => {
    if (e) e.stopPropagation();
    if (!effectiveStore?.id) {
      // No store resolved anywhere in the app yet — fall back to the shop
      // page, same as the original navigate-only behavior, rather than
      // silently failing.
      navigate('/restaurants');
      return;
    }
    const { isClosed } = getStoreClosedStatus();
    if (isClosed) {
      setShowStoreClosedModal(true);
      return;
    }
    if (item.isWeightBased) {
      setWeightModalItem(item);
      return;
    }
    if (!isLoggedIn()) {
      pendingActionRef.current = { type: 'simple', item };
      setShowAuthModal(true);
      return;
    }
    addSimpleItem(item);
  };

  const handleWeightConfirm = (weightedProduct) => {
    const item = weightModalItem;
    setWeightModalItem(null);
    if (!item) return;
    const { isClosed } = getStoreClosedStatus();
    if (isClosed) {
      setShowStoreClosedModal(true);
      return;
    }
    const option = { value: weightedProduct.selectedWeight, label: weightedProduct.weightLabel };
    if (!isLoggedIn()) {
      pendingActionRef.current = { type: 'weighted', item, option };
      setShowAuthModal(true);
      return;
    }
    addWeightedItem(item, option);
  };

  const handlePreOrderFromClosedModal = () => {
    setShowStoreClosedModal(false);
    setPreOrder(true);
    navigate('/restaurants');
  };

  const weightModalProduct = weightModalItem ? {
    id: weightModalItem.id,
    name: weightModalItem.title,
    image: weightModalItem.image,
    price: weightModalItem.priceValue,
    flatPrice: weightModalItem.priceValue,
    unit: weightModalItem.weightUnitLabel,
    weight_config: { weight_options: weightModalItem.weightOptions },
    cartItemKey: String(weightModalItem.id)
  } : null;

  const formattedStore = effectiveStore ? {
    ...effectiveStore,
    name: effectiveStore.name || effectiveStore.branch_name || 'Store',
    location: effectiveStore.location || [effectiveStore.address, effectiveStore.city, effectiveStore.state].filter(Boolean).join(', ') || 'Store Location'
  } : null;

  const storeStatus = effectiveStore?.week_schedule ? getStoreStatus(effectiveStore.week_schedule) : null;
  const supportsPreOrder = Boolean(effectiveStore?.allow_pre_order || effectiveStore?.pre_order_enabled);

  return {
    effectiveStore,
    isLoggedIn,
    handleAddToCart,
    showAuthModal,
    closeAuthModal: () => { setShowAuthModal(false); pendingActionRef.current = null; },
    handleAuthSuccess,
    weightModalProduct,
    isWeightModalOpen: !!weightModalItem,
    closeWeightModal: () => setWeightModalItem(null),
    handleWeightConfirm,
    showStoreClosedModal,
    closeStoreClosedModal: () => setShowStoreClosedModal(false),
    formattedStore,
    storeStatus,
    supportsPreOrder,
    handlePreOrderFromClosedModal
  };
};

// Renders the shared modals (auth + weight + store closed) that every non-T16
// template's product section uses — one instance per component via this
// helper so each per-template component only needs one extra line in its
// JSX.
export const SpecialtyCartModals = ({ cart }) => (
  <>
    <AuthModal
      isOpen={cart.showAuthModal}
      onClose={cart.closeAuthModal}
      onAuthSuccess={cart.handleAuthSuccess}
      branchId={cart.effectiveStore?.id}
    />
    <WeightSelectionModal
      isOpen={cart.isWeightModalOpen}
      onClose={cart.closeWeightModal}
      product={cart.weightModalProduct}
      onConfirm={cart.handleWeightConfirm}
    />
    <StoreClosedModal
      isOpen={cart.showStoreClosedModal}
      onClose={cart.closeStoreClosedModal}
      store={cart.formattedStore}
      storeStatus={cart.storeStatus}
      onPreOrder={cart.handlePreOrderFromClosedModal}
      supportsPreOrder={cart.supportsPreOrder}
      hasOtherStores={false}
    />
  </>
);
